import { useState, useRef, useEffect } from 'react'
import type {
  ClubSummaryProps,
  FormResult,
  ManagerOption,
} from '../types'

// =============================================================================
// Sub-components
// =============================================================================

function ManagerDropdown({ selected, managers, onChange }: {
  selected: string
  managers: ManagerOption[]
  onChange: (id: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const selectedManager = managers.find(m => m.id === selected)
  const label = selected === 'autopick' ? 'Autopick' : selectedManager?.displayName ?? 'Select Manager'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium
                   transition-colors cursor-pointer
                   border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900
                   text-zinc-700 dark:text-zinc-300
                   hover:border-zinc-300 dark:hover:border-zinc-600"
      >
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="8" cy="5" r="3" />
          <path d="M2 14c0-3 2.5-5 6-5s6 2 6 5" />
        </svg>
        <span className="truncate max-w-[160px]">{label}</span>
        <svg className={`w-3.5 h-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64
                        rounded-xl border border-zinc-200 dark:border-zinc-700
                        bg-white dark:bg-zinc-900 shadow-lg shadow-zinc-200/50 dark:shadow-zinc-950/50
                        overflow-hidden">
          <div className="py-1 max-h-72 overflow-y-auto">
            {managers.map(manager => (
              <button
                key={manager.id}
                type="button"
                onClick={() => { onChange(manager.id); setIsOpen(false) }}
                className={`w-full text-left px-3.5 py-2 text-sm cursor-pointer
                            transition-colors flex items-center gap-2
                            ${selected === manager.id
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-medium'
                              : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
              >
                <span className="flex items-center justify-center w-6 h-6 rounded-full
                                bg-zinc-100 dark:bg-zinc-800 text-[10px] font-bold
                                text-zinc-600 dark:text-zinc-400">
                  {manager.initials}
                </span>
                <span>{manager.displayName}</span>
              </button>
            ))}

            <div className="border-t border-zinc-100 dark:border-zinc-800 my-1" />

            <button
              type="button"
              onClick={() => { onChange('autopick'); setIsOpen(false) }}
              className={`w-full text-left px-3.5 py-2 text-sm cursor-pointer
                          transition-colors flex items-center gap-2
                          ${selected === 'autopick'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-medium'
                            : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full
                              bg-zinc-100 dark:bg-zinc-800 text-[10px]
                              text-zinc-600 dark:text-zinc-400">
                *
              </span>
              <span>Autopick</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function FormBadge({ result }: { result: FormResult }) {
  const styles: Record<FormResult, string> = {
    W: 'bg-emerald-500 text-white',
    D: 'bg-zinc-400 dark:bg-zinc-500 text-white',
    L: 'bg-red-500 text-white',
  }

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold ${styles[result]}`}>
      {result}
    </span>
  )
}

function ClubLogo({ logo, name, size = 'md' }: { logo: string; name: string; size?: 'sm' | 'md' }) {
  const [imgError, setImgError] = useState(false)
  const sizeClass = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'

  // Check if logo is a URL
  const isUrl = logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('/')

  // Get initials for fallback
  const initials = name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase()

  // Show initials if not a URL or if image failed to load
  if (!isUrl || imgError) {
    return (
      <span title={name} className={`${sizeClass} flex items-center justify-center rounded bg-zinc-100 dark:bg-zinc-800 text-[8px] font-bold text-zinc-500 dark:text-zinc-400`}>
        {initials}
      </span>
    )
  }

  return (
    <img
      src={logo}
      alt={name}
      title={name}
      className={`${sizeClass} object-contain`}
      onError={() => setImgError(true)}
    />
  )
}

// =============================================================================
// Sort types
// =============================================================================

type SortKey = 'rank' | 'left' | 'earned' | 'points' | 'gd' | 'form'
type SortDir = 'asc' | 'desc'

const SORT_OPTIONS: { key: SortKey; label: string; defaultDir: SortDir }[] = [
  { key: 'rank', label: 'Table Position', defaultDir: 'asc' },
  { key: 'left', label: 'Picks Left', defaultDir: 'desc' },
  { key: 'earned', label: 'Pts Earned', defaultDir: 'desc' },
  { key: 'points', label: 'League Pts', defaultDir: 'desc' },
  { key: 'gd', label: 'Goal Diff', defaultDir: 'desc' },
  { key: 'form', label: 'Form', defaultDir: 'desc' },
]

function formScore(form: FormResult[]): number {
  return form.slice(-5).reduce((sum, r) => sum + (r === 'W' ? 3 : r === 'D' ? 1 : 0), 0)
}

// =============================================================================
// Main Component
// =============================================================================

export function ClubSummary({
  eplTable,
  managerStats,
  managers,
  currentUserId,
  competitionCode = 'League',
  teamPickLimit = 2,
  onManagerChange,
}: ClubSummaryProps) {
  const [selectedManager, setSelectedManager] = useState(currentUserId)
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const stats = managerStats[selectedManager] ?? []
  const statsMap = new Map(stats.map(s => [s.teamId, s]))

  function handleManagerChange(id: string) {
    setSelectedManager(id)
    onManagerChange?.(id)
  }

  // Build sorted table
  const sortedTable = [...eplTable].sort((a, b) => {
    const statA = statsMap.get(a.teamId)
    const statB = statsMap.get(b.teamId)
    const leftA = teamPickLimit - (statA?.timesPicked ?? 0)
    const leftB = teamPickLimit - (statB?.timesPicked ?? 0)
    const earnedA = statA?.points ?? 0
    const earnedB = statB?.points ?? 0
    const dir = sortDir === 'asc' ? 1 : -1

    switch (sortKey) {
      case 'rank': return (a.rank - b.rank) * dir
      case 'left': return (leftA - leftB) * dir
      case 'earned': return (earnedA - earnedB) * dir
      case 'points': return (a.points - b.points) * dir
      case 'gd': return (a.goalDifference - b.goalDifference) * dir
      case 'form': return (formScore(a.form) - formScore(b.form)) * dir
      default: return 0
    }
  })

  const thBase = 'px-2 py-2 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap'
  const thClass = `${thBase} text-zinc-400 dark:text-zinc-500`

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      const opt = SORT_OPTIONS.find(o => o.key === key)
      setSortKey(key)
      setSortDir(opt?.defaultDir ?? 'desc')
    }
  }

  function SortTh({ sortable, label, align = 'center', className = '' }: {
    sortable: SortKey
    label: string
    align?: 'left' | 'center'
    className?: string
  }) {
    const isActive = sortKey === sortable
    return (
      <th
        onClick={() => handleSort(sortable)}
        className={`${thBase} text-${align} cursor-pointer select-none transition-colors ${className}
                    ${isActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300'}`}
      >
        <span className="inline-flex items-center gap-0.5">
          {label}
          {isActive && (
            <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 10 10" fill="currentColor">
              {sortDir === 'asc'
                ? <path d="M5 2L1 7h8L5 2z" />
                : <path d="M5 8L1 3h8L5 8z" />}
            </svg>
          )}
        </span>
      </th>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6
                    sm:px-6
                    md:px-0">
      {/* Header with dropdown */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          {competitionCode}
        </h1>
        <ManagerDropdown
          selected={selectedManager}
          managers={managers}
          onChange={handleManagerChange}
        />
      </div>

      {/* League Table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800
                      bg-white dark:bg-zinc-900 overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-zinc-100 dark:border-zinc-800">
              <SortTh sortable="rank" label="#" className="w-10" />
              <th className={`${thClass} text-left w-36`}>Club</th>
              <SortTh sortable="left" label="Left" className="border-l border-zinc-100 dark:border-zinc-800" />
              <SortTh sortable="earned" label="Earned" />
              <th className={`${thClass} text-center w-10 border-l border-zinc-100 dark:border-zinc-800`}>PL</th>
              <th className={`${thClass} text-center`}>W</th>
              <th className={`${thClass} text-center`}>D</th>
              <th className={`${thClass} text-center`}>L</th>
              <th className={`${thClass} text-center`}>+/-</th>
              <SortTh sortable="gd" label="GD" />
              <SortTh sortable="points" label="PTS" />
              <SortTh sortable="form" label="Form" />
              <th className={`${thClass} text-center`}>Next</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {sortedTable.map((entry) => {
              const stat = statsMap.get(entry.teamId)
              const timesPicked = stat?.timesPicked ?? 0
              const picksLeft = teamPickLimit - timesPicked
              const pointsEarned = stat?.points ?? 0

              return (
                <tr key={entry.teamId} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                  <td className="px-2 py-2.5 text-center text-sm font-bold tabular-nums text-zinc-400 dark:text-zinc-500">
                    {entry.rank}
                  </td>
                  <td className="px-2 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <ClubLogo logo={entry.logo} name={entry.shortName} />
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {entry.shortName}
                      </span>
                    </div>
                  </td>
                  <td className={`px-2 py-2.5 text-center text-sm tabular-nums font-bold border-l border-zinc-100 dark:border-zinc-800
                                  ${picksLeft === 0
                                    ? 'text-zinc-300 dark:text-zinc-600'
                                    : picksLeft === teamPickLimit
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-amber-500 dark:text-amber-400'}`}>
                    {picksLeft}
                  </td>
                  <td className={`px-2 py-2.5 text-center text-sm tabular-nums font-bold
                                  ${pointsEarned === 0
                                    ? 'text-zinc-300 dark:text-zinc-600'
                                    : 'text-emerald-600 dark:text-emerald-400'}`}>
                    {pointsEarned}
                  </td>
                  <td className="px-2 py-2.5 text-center text-xs tabular-nums font-medium text-zinc-400 dark:text-zinc-500 border-l border-zinc-100 dark:border-zinc-800">
                    {entry.played}
                  </td>
                  <td className="px-2 py-2.5 text-center text-xs tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                    {entry.wins}
                  </td>
                  <td className="px-2 py-2.5 text-center text-xs tabular-nums font-medium text-zinc-500 dark:text-zinc-400">
                    {entry.draws}
                  </td>
                  <td className="px-2 py-2.5 text-center text-xs tabular-nums font-medium text-zinc-500 dark:text-zinc-400">
                    {entry.losses}
                  </td>
                  <td className="px-2 py-2.5 text-center text-xs tabular-nums font-medium text-zinc-500 dark:text-zinc-400">
                    {entry.goalsFor}-{entry.goalsAgainst}
                  </td>
                  <td className={`px-2 py-2.5 text-center text-xs tabular-nums font-bold
                                  ${entry.goalDifference > 0
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : entry.goalDifference < 0
                                      ? 'text-red-500 dark:text-red-400'
                                      : 'text-zinc-400 dark:text-zinc-500'}`}>
                    {entry.goalDifference > 0 ? '+' : ''}{entry.goalDifference}
                  </td>
                  <td className="px-2 py-2.5 text-center text-sm tabular-nums font-bold text-zinc-900 dark:text-zinc-100">
                    {entry.points}
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-0.5 justify-center">
                      {entry.form.length > 0 ? (
                        entry.form.map((result, i) => (
                          <FormBadge key={i} result={result} />
                        ))
                      ) : (
                        <span className="text-zinc-300 dark:text-zinc-600">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {entry.nextOpponent ? (
                      <div className="flex items-center justify-center">
                        <ClubLogo logo={entry.nextOpponent.logo} name={entry.nextOpponent.shortName} size="sm" />
                      </div>
                    ) : (
                      <span className="text-zinc-300 dark:text-zinc-600">-</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
